import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Menu, X, Phone, Mail, MapPin, Award, Target, Users, Briefcase, Package, History, Globe, Network, Settings, ArrowRight, BookOpen, FileText, Shield, Lightbulb, TrendingUp, Linkedin, Globe2 } from 'lucide-react';

// ==========================================
// KOMPONEN ANIMASI & SPLASH SCREEN (Tetap)
// ==========================================

const AnimatedCounter = ({ target, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(0);
  const startTimeRef = useRef(null);

  useEffect(() => {
    const animateCount = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const progress = Math.min((timestamp - startTimeRef.current) / duration, 1);
      
      const easeOutQuad = t => t * (2 - t);
      const easedProgress = easeOutQuad(progress);
      
      countRef.current = Math.floor(easedProgress * target);
      setCount(countRef.current);
      
      if (progress < 1) {
        requestAnimationFrame(animateCount);
      } else {
        setCount(target);
      }
    };
    
    requestAnimationFrame(animateCount);
    
    return () => {
      startTimeRef.current = null;
    };
  }, [target, duration]);

  return <span>{count}{suffix}</span>;
};

const SplashScreen = ({ onComplete, lang }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onComplete();
    }, 3000);

    return () => clearTimeout(timer);
  }, [onComplete]);

  // Teks Splash Screen berdasarkan bahasa awal
  const splashText = lang === 'id' 
    ? { title: "The Horizon Indonesia", subtitle: "Membangun Masa Depan Bersama" }
    : { title: "The Horizon Indonesia", subtitle: "Building Future Together" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-orange-600 via-orange-500 to-amber-600">
      <div className="text-center animate-fade-in">
        <div className="relative">
          <div className="w-40 h-40 bg-white rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-2xl animate-bounce-slow overflow-hidden p-6">
            <img 
              src="/images/horizon-logo-new.png" 
              alt="The Horizon Logo" 
              className="w-full h-full object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<span class="text-orange-600 font-bold text-7xl">TH</span>';
              }}
            />
          </div>
          <div className="absolute -top-2 -right-2 w-12 h-12 bg-amber-400 rounded-full animate-ping"></div>
        </div>
        <h1 className="text-5xl font-bold text-white mb-2">{splashText.title}</h1>
        <p className="text-xl text-orange-100">{splashText.subtitle}</p>
      </div>
      <style jsx>{`
        @keyframes fade-in {
          0% { opacity: 0; transform: translateY(30px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        .animate-fade-in {
          animation: fade-in 1.5s ease-out;
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

// ==========================================
// DATA TERJEMAHAN
// ==========================================

const translations = {
  id: {
    nav: {
      home: "Beranda",
      about: "Tentang Kami",
      vision: "Visi Misi",
      values: "Nilai",
      expertise: "Keahlian",
      credentials: "Kredensial",
      products: "Produk",
      team: "Tim",
      contact: "Kontak"
    },
    hero: {
      badge: "Yayasan Horizon Adicita Nusantara",
      title: "Membangun Masa Depan Bersama",
      subtitle: "Lembaga penelitian terpercaya untuk kebijakan strategis Indonesia",
      btnProducts: "Lihat Produk",
      btnContact: "Hubungi Kami"
    },
    stats: {
      clients: "Klien Puas",
      team: "Tim Profesional",
      years: "Tahun Pengalaman",
      products: "Produk Inovatif"
    },
    about: {
      badge: "Tentang Kami",
      title: "The Horizon Indonesia",
      desc: "The Horizon Indonesia merupakan Yayasan Sosial yang bergerak di bidang penelitian sosial, politik, dan ekonomi dalam ranah kebijakan strategis Indonesia.",
      statsClients: "Klien Puas",
      statsTeam: "Tim Profesional",
      cardTitle: "Your Trusted Partner",
      cardDesc: "Mitra terpercaya dalam pengembangan kebijakan strategis"
    },
    history: {
      title: "Sejarah Kami",
      subtitle: "Perjalanan kami dalam membangun masa depan Indonesia",
      story: "The Horizon Indonesia diawali sebagai sebuah forum akademis yang berfungsi membahas berbagai isu politik internasional dan juga nasional yang pertama tercetus pada tahun 2019. Seiring waktu, forum ini kemudian meresmikan diri menjadi yayasan, dengan nama Yayasan Horizon Adicita Nusantara, per SK Kemenkumham nomor AHU-0014349.AH.01.04.Tahun 2023 tertanggal 5 September 2023."
    },
    visionMission: {
      vision: "Visi Kami",
      mission: "Misi Kami",
      visionText: "Menjadi lembaga penelitian yang mendorong pengembangan kebijakan pertahanan, keamanan, sosial, politik, dan ekonomi yang menopang kepentingan nasional.",
      missionItems: [
        "Mendukung Pengembangan Kebijakan Nasional melalui kajian pertahanan, keamanan, sosial, politik, dan ekonomi yang berbasis bukti dan wawasan yang akurat",
        "Mendorong kolaborasi dan kerjasama multi-disiplin ilmu untuk melihat masalah dari berbagai perspektif, menjamin solusi yang holistik dan inovatif",
        "Menyediakan rekomendasi kebijakan yang inovatif dan relevan bagi pemangku kepentingan terkait dengan prinsip kemajuan nasional"
      ]
    },
    values: {
      badge: "Nilai-Nilai Kami",
      title: "Our Values",
      subtitle: "Nilai-nilai yang memandu setiap langkah kami dalam memberikan solusi terbaik",
      items: [
        { 
          title: "EVIDENCE BASED", 
          desc: "The Horizon Indonesia berorientasi pada bukti faktual",
          detail: "Kami berkomitmen untuk menghasilkan penelitian yang didasarkan pada data dan bukti yang valid. Setiap rekomendasi kebijakan yang kami berikan didukung oleh analisis mendalam dan fakta yang dapat diverifikasi."
        },
        { 
          title: "MULTIDISCIPLINARY", 
          desc: "The Horizon Indonesia melihat suatu isu dari berbagai sudut pandang",
          detail: "Kami menggabungkan berbagai disiplin ilmu untuk memberikan perspektif holistik dalam menganalisis isu-isu kompleks. Pendekatan ini memungkinkan kami mengidentifikasi keterkaitan antar aspek yang mungkin terlewatkan dalam analisis sektoral."
        },
        { 
          title: "SOLUTION BASED", 
          desc: "The Horizon Indonesia berorientasi terhadap solusi praktikal",
          detail: "Kami tidak hanya mengidentifikasi masalah, tetapi juga fokus pada pengembangan solusi yang praktis dan dapat diimplementasikan. Setiap rekomendasi dirancang untuk memberikan dampak positif yang nyata."
        }
      ]
    },
    expertise: {
      badge: "Keahlian Kami",
      title: "Our Expertise",
      subtitle: "Area keahlian kami untuk mendukung kebutuhan strategis Anda",
      categories: {
        strategic: "Strategic Studies",
        economic: "Economic Studies",
        other: "Other Solutions"
      },
      customTitle: "Custom Solutions",
      customDesc: "Kami menyediakan solusi kustom yang disesuaikan dengan kebutuhan spesifik klien kami. Hubungi kami untuk diskusi lebih lanjut tentang tantangan yang Anda hadapi.",
      contactBtn: "Hubungi Kami"
    },
    credentials: {
      badge: "Kredensial Kami",
      title: "Our Credentials",
      subtitle: "Institusi dan lembaga terpercaya yang telah mempercayai kami",
      detailBtn: "Lihat Detail Kerjasama",
      footer: "Mitra Strategis Terpercaya"
    },
    products: {
      title: "Our Products",
      subtitle: "Katalog produk dan layanan berkualitas untuk kebutuhan strategis Anda",
      otherTitle: "Produk & Layanan Lainnya",
      typePub: "Publikasi",
      typeService: "Layanan",
      btnBuy: "Hubungan Kami",
      btnContact: "Hubungi Kami"
    },
    team: {
      title: "Our Teams",
      subtitle: "Dipimpin oleh para profesional berpengalaman dan berdedikasi"
    },
    contact: {
      title: "Hubungi Kami",
      subtitle: "Kami siap membantu mewujudkan kebutuhan strategis Anda",
      phone: "Telepon",
      email: "Email",
      address: "Alamat",
      waBtn: "Chat via WhatsApp"
    },
    footer: {
      desc: "Membangun masa depan bersama melalui penelitian dan kebijakan strategis.",
      copyright: "© 2024 The Horizon Indonesia. All rights reserved.",
      links: "Quick Links",
      connect: "Connect With Us"
    }
  },
  en: {
    nav: {
      home: "Home",
      about: "About Us",
      vision: "Vision & Mission",
      values: "Values",
      expertise: "Expertise",
      credentials: "Credentials",
      products: "Products",
      team: "Team",
      contact: "Contact"
    },
    hero: {
      badge: "Yayasan Horizon Adicita Nusantara",
      title: "Building Future Together",
      subtitle: "A trusted research institute for Indonesia's strategic policy",
      btnProducts: "View Products",
      btnContact: "Contact Us"
    },
    stats: {
      clients: "Satisfied Clients",
      team: "Professional Team",
      years: "Years Experience",
      products: "Innovative Products"
    },
    about: {
      badge: "About Us",
      title: "The Horizon Indonesia",
      desc: "The Horizon Indonesia is a Social Foundation engaged in social, political, and economic research within the realm of Indonesia's strategic policy.",
      statsClients: "Satisfied Clients",
      statsTeam: "Professional Team",
      cardTitle: "Your Trusted Partner",
      cardDesc: "Trusted partner in strategic policy development"
    },
    history: {
      title: "Our History",
      subtitle: "Our journey in building Indonesia's future",
      story: "The Horizon Indonesia began as an academic forum functioning to discuss various international and national political issues, first conceived in 2019. Over time, this forum then officially established itself as a foundation, with the name Yayasan Horizon Adicita Nusantara, based on SK Kemenkumham No. AHU-0014349.AH.01.04.Year 2023 dated September 5, 2023."
    },
    visionMission: {
      vision: "Our Vision",
      mission: "Our Mission",
      visionText: "To become a research institute that encourages the development of defense, security, social, political, and economic policies that support national interests.",
      missionItems: [
        "Supporting National Policy Development through evidence-based defense, security, social, political, and economic studies with accurate insights",
        "Encouraging collaboration and multi-disciplinary cooperation to view problems from various perspectives, ensuring holistic and innovative solutions",
        "Providing innovative and relevant policy recommendations for stakeholders regarding the principle of national progress"
      ]
    },
    values: {
      badge: "Our Values",
      title: "Our Values",
      subtitle: "Values that guide every step we take in providing the best solutions",
      items: [
        { 
          title: "EVIDENCE BASED", 
          desc: "The Horizon Indonesia is oriented towards factual evidence",
          detail: "We are committed to producing research based on valid data and evidence. Every policy recommendation we provide is supported by in-depth analysis and verifiable facts."
        },
        { 
          title: "MULTIDISCIPLINARY", 
          desc: "The Horizon Indonesia views an issue from various perspectives",
          detail: "We combine various disciplines to provide a holistic perspective in analyzing complex issues. This approach allows us to identify linkages between aspects that might be missed in sectoral analysis."
        },
        { 
          title: "SOLUTION BASED", 
          desc: "The Horizon Indonesia is oriented towards practical solutions",
          detail: "We not only identify problems but also focus on developing practical and implementable solutions. Every recommendation is designed to provide a real positive impact."
        }
      ]
    },
    expertise: {
      badge: "Our Expertise",
      title: "Our Expertise",
      subtitle: "Our areas of expertise to support your strategic needs",
      categories: {
        strategic: "Strategic Studies",
        economic: "Economic Studies",
        other: "Other Solutions"
      },
      customTitle: "Custom Solutions",
      customDesc: "We provide custom solutions tailored to our clients' specific needs. Contact us for further discussion about the challenges you face.",
      contactBtn: "Contact Us"
    },
    credentials: {
      badge: "Our Credentials",
      title: "Our Credentials",
      subtitle: "Trusted institutions that have entrusted us",
      detailBtn: "View Partnership Details",
      footer: "Trusted Strategic Partners"
    },
    products: {
      title: "Our Products",
      subtitle: "Catalog of quality products and services for your strategic needs",
      otherTitle: "Other Products & Services",
      typePub: "Publication",
      typeService: "Service",
      btnBuy: "Contact Us",
      btnContact: "Contact Us"
    },
    team: {
      title: "Our Teams",
      subtitle: "Led by experienced and dedicated professionals"
    },
    contact: {
      title: "Contact Us",
      subtitle: "We are ready to help realize your strategic needs",
      phone: "Phone",
      email: "Email",
      address: "Address",
      waBtn: "Chat via WhatsApp"
    },
    footer: {
      desc: "Building the future together through research and strategic policy.",
      copyright: "© 2024 The Horizon Indonesia. All rights reserved.",
      links: "Quick Links",
      connect: "Connect With Us"
    }
  }
};

const CompanyProfile = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [activeValue, setActiveValue] = useState(null);
  const [activeCredential, setActiveCredential] = useState(null);
  const [activeProduct, setActiveProduct] = useState(0);
  
  // State Bahasa: Default 'id'
  const [language, setLanguage] = useState('id');
  
  const mapRef = useRef(null);

  // Mengambil teks berdasarkan bahasa yang dipilih
  const t = translations[language];

  // Menggabungkan data statis dengan teks yang sudah diterjemahkan
  const localizedData = useMemo(() => {
    const baseData = {
      about: {
        logo: "/images/horizon-logo-new.png",
        description: t.about.desc
      },
      history: {
        year: "2019",
        story: t.history.story
      },
      vision: t.visionMission.visionText,
      mission: t.visionMission.missionItems,
      values: [
        { 
          title: t.values.items[0].title, 
          desc: t.values.items[0].desc,
          icon: Globe,
          color: "from-blue-600 to-blue-400",
          detail: t.values.items[0].detail
        },
        { 
          title: t.values.items[1].title, 
          desc: t.values.items[1].desc,
          icon: Network,
          color: "from-orange-600 to-yellow-200",
          detail: t.values.items[1].detail
        },
        { 
          title: t.values.items[2].title, 
          desc: t.values.items[2].desc,
          icon: Settings,
          color: "from-red-600 to-pink-600",
          detail: t.values.items[2].detail
        }
      ],
      expertise: [
        { name: "Geopolitical Studies", category: "strategic", color: "bg-gradient-to-br from-blue-600 to-blue-400", icon: Globe, desc: language === 'id' ? "Analisis dinamika geopolitik regional dan global yang mempengaruhi kepentingan nasional Indonesia." : "Analysis of regional and global geopolitical dynamics affecting Indonesia's national interests." },
        { name: "Security Studies", category: "strategic", color: "bg-gradient-to-br from-blue-500 to-blue-300", icon: Shield, desc: language === 'id' ? "Studi mendalam tentang isu-isu keamanan tradisional dan non-tradisional yang dihadapi Indonesia." : "In-depth study of traditional and non-traditional security issues facing Indonesia." },
        { name: "Strategic Studies", category: "strategic", color: "bg-gradient-to-br from-indigo-600 to-indigo-400", icon: Target, desc: language === 'id' ? "Pengembangan strategi jangka panjang untuk menghadapi tantangan domestik dan internasional." : "Developing long-term strategies to address domestic and international challenges." },
        { name: "Political Economy Studies", category: "strategic", color: "bg-gradient-to-br from-blue-400 to-cyan-300", icon: TrendingUp, desc: language === 'id' ? "Analisis interaksi antara politik dan ekonomi dalam konteks kebijakan publik Indonesia." : "Analysis of the interaction between politics and economics in the context of Indonesian public policy." },
        { name: "Government Affairs", category: "strategic", color: "bg-gradient-to-br from-cyan-500 to-blue-300", icon: Briefcase, desc: language === 'id' ? "Layanan konsultasi untuk navigasi kompleksitas birokrasi dan proses kebijakan pemerintah." : "Consulting services for navigating bureaucratic complexities and government policy processes." },
        { name: "Market Research", category: "economic", color: "bg-gradient-to-br from-amber-600 to-amber-400", icon: TrendingUp, desc: language === 'id' ? "Penelitian pasar komprehensif untuk mendukung pengambilan keputusan bisnis yang strategis." : "Comprehensive market research to support strategic business decision-making." },
        { name: "Development Economics", category: "economic", color: "bg-gradient-to-br from-yellow-600 to-yellow-400", icon: Award, desc: language === 'id' ? "Analisis kebijakan pembangunan ekonomi untuk pertumbuhan yang inklusif dan berkelanjutan." : "Analysis of economic development policy for inclusive and sustainable growth." },
        { name: "International Economics", category: "economic", color: "bg-gradient-to-br from-orange-500 to-yellow-400", icon: Globe, desc: language === 'id' ? "Studi tentang perdagangan internasional, investasi, dan keuangan global dalam konteks Indonesia." : "Study of international trade, investment, and global finance in the context of Indonesia." },
        { name: "ESG Studies", category: "economic", color: "bg-gradient-to-br from-orange-600 to-yellow-400", icon: Lightbulb, desc: language === 'id' ? "Penelitian tentang Environmental, Social, and Governance (ESG) dan implikasinya bagi bisnis." : "Research on Environmental, Social, and Governance (ESG) and its implications for business." },
        { name: "Economy Regional", category: "economic", color: "bg-gradient-to-br from-orange-600 to-yellow-200", icon: MapPin, desc: language === 'id' ? "Analisis ekonomi regional untuk mengidentifikasi potensi dan tantangan di berbagai wilayah Indonesia." : "Regional economic analysis to identify potentials and challenges in various regions of Indonesia." }
      ],
      credentials: [
        { name: "Markas Besar TNI", logo: "/images/markas-tni-new.png", color: "from-green-700 to-green-900", description: language === 'id' ? "Kerjasama strategis dalam bidang pertahanan dan keamanan nasional" : "Strategic cooperation in national defense and security" },
        { name: "Sekolah Staf dan Komando TNI", logo: "/images/sekolah-staf-new.png", color: "from-blue-700 to-blue-900", description: language === 'id' ? "Pengembangan kurikulum dan materi pelatihan strategis" : "Curriculum development and strategic training materials" },
        { name: "Markas Besar TNI AU", logo: "/images/markas-besar-tni-au-new.png", color: "from-sky-600 to-sky-800", description: language === 'id' ? "Analisis kebijakan pertahanan udara dan teknologi militer" : "Air defense policy analysis and military technology" },
        { name: "Komando Operasi Udara Nasional", logo: "/images/komando-operasi-logo.png", color: "from-indigo-600 to-indigo-800", description: language === 'id' ? "Studi operasional dan evaluasi sistem pertahanan udara" : "Operational studies and air defense system evaluation" },
        { name: "Kementerian Kelautan dan Perikanan", logo: "/images/kementrian-kelautan-new.png", color: "from-cyan-600 to-cyan-800", description: language === 'id' ? "Penelitian kebijakan kelautan dan pengembangan ekonomi biru" : "Marine policy research and blue economy development" },
        { name: "Kementerian Kebudayaan", logo: "/images/kementrian-kebudayaan-logo.png", color: "from-purple-600 to-purple-800", description: language === 'id' ? "Analisis dampak sosial-budaya dari kebijakan nasional" : "Analysis of socio-cultural impacts of national policies" }
      ],
      products: [
        {
          id: 1,
          title: "Rusia - Ukraina",
          type: "book",
          icon: BookOpen,
          image: "/images/rusia-ukraina.png",
          gradient: "from-red-600 to-orange-600",
          description: language === 'id' ? "Analisis mendalam tentang konflik geopolitik Rusia-Ukraina dan dampaknya terhadap keamanan global" : "In-depth analysis of the Russia-Ukraine geopolitical conflict and its impact on global security",
          category: "Geopolitical Studies",
          details: language === 'id' ? "Buku ini menyajikan analisis komprehensif tentang konflik Rusia-Ukraina dari perspektif sejarah, politik, dan keamanan. Dilengkapi dengan data dan peta strategis untuk memberikan pemahaman yang lebih baik tentang dinamika geopolitik di Eropa Timur." : "This book presents a comprehensive analysis of the Russia-Ukraine conflict from historical, political, and security perspectives. Equipped with strategic data and maps to provide a better understanding of geopolitical dynamics in Eastern Europe."
        },
        {
          id: 2,
          title: "Asistensi Kelembagaan",
          type: "service",
          icon: Users,
          image: "/images/asistensi-kelembagaan.jpg",
          gradient: "from-blue-600 to-indigo-600",
          description: language === 'id' ? "Layanan konsultasi untuk pengembangan kapasitas kelembagaan di berbagai sektor" : "Consulting services for institutional capacity development in various sectors",
          category: "Institutional Development",
          details: language === 'id' ? "Kami menyediakan layanan asistensi kelembagaan yang komprehensif, meliputi analisis organisasi, perancangan struktur, pengembangan SOP, dan pelatihan sumber daya manusia untuk meningkatkan efektivitas dan efisiensi organisasi." : "We provide comprehensive institutional assistance services, including organizational analysis, structure design, SOP development, and human resource training to improve organizational effectiveness and efficiency."
        },
        {
          id: 3,
          title: "Asistensi Konferensi Lokal & Internasional",
          type: "service",
          icon: Globe,
          image: "/images/asistensi-konferensi-lokal.jpg",
          gradient: "from-purple-600 to-pink-600",
          description: language === 'id' ? "Fasilitasi dan dukungan teknis untuk penyelenggaraan konferensi tingkat lokal dan internasional" : "Facilitation and technical support for organizing local and international conferences",
          category: "Event Management",
          details: language === 'id' ? "Tim kami berpengalaman dalam menyelenggarakan konferensi tingkat lokal maupun internasional. Dari perencanaan awal hingga pelaksanaan, kami menyediakan dukungan teknis, manajemen logistik, dan koordinasi dengan berbagai pemangku kepentingan." : "Our team is experienced in organizing local and international conferences. From initial planning to execution, we provide technical support, logistics management, and coordination with various stakeholders."
        },
        {
          id: 4,
          title: "Pembuatan Produk Analisa",
          type: "service",
          icon: FileText,
          image: "/images/pembuatan-produk-analisa.png",
          gradient: "from-emerald-600 to-teal-600",
          description: language === 'id' ? "Pengembangan produk analisis kustom sesuai kebutuhan spesifik klien" : "Development of custom analysis products according to specific client needs",
          category: "Custom Analysis",
          details: language === 'id' ? "Kami menghasilkan berbagai produk analisis, mulai dari laporan singkat hingga studi mendalam, yang disesuaikan dengan kebutuhan spesifik klien. Setiap produk didasarkan pada metodologi penelitian yang kredibel dan data yang akurat." : "We produce various analysis products, from brief reports to in-depth studies, tailored to the specific needs of clients. Each product is based on credible research methodologies and accurate data."
        },
      ],
      team: [
        { name: "Defantra Ramadhan", position: language === 'id' ? "Chief Executive Officer" : "Chief Executive Officer", initials: "DR", color: "from-orange-500 to-red-500", photo: "/images/defantra.jpg" },
        { name: "Humamvidi Hunafa", position: "Vice-Director General Treasurer", initials: "HH", color: "from-blue-500 to-indigo-500", photo: "/images/humamvidi.jpg" },
        { name: "Dimas Yudhistira", position: "Vice-Director Head of Politics & Economics", initials: "DY", color: "from-purple-500 to-pink-500", photo: "/images/dimas.png" },
        { name: "M Rifqi Daneswara", position: "Senior Analyst Security & Defense", initials: "MR", color: "from-green-500 to-teal-500", photo: "/images/mrifqi.jpg" },
        { name: "Jonthan Jordan", position: "Senior Analyst Security & Defense", initials: "JJ", color: "from-cyan-500 to-blue-500", photo: "/images/jonathan.jpg" },
        { name: "Akbar Muhammad", position: "Senior Analyst Politics & Economics", initials: "AM", color: "from-amber-500 to-orange-500", photo: "/images/akbar.png" },
        { name: "Leon Aruan", position: "Senior Analyst Politics & Economics", initials: "LA", color: "from-red-500 to-pink-500", photo: "/images/leon.png" },
        { name: "Catherine", position: "Senior Analyst Security & Defense", initials: "CT", color: "from-violet-500 to-purple-500", photo: "/images/catherine.png" },
        { name: "Risang Piawang", position: "Senior Analyst Politics & Economics", initials: "RP", color: "from-emerald-500 to-green-500", photo: "/images/risang.jpg" },
        { name: "Lazarus Andja Karuna", position: "Senior Analyst Politics & Economics", initials: "LA", color: "from-sky-500 to-cyan-500", photo: "/images/lazarus.jpg" }
      ],
      contact: {
        phone: "+62 812-1051-4213",
        email: "admin@thehorizon.or.id",
        address: "Jl. Sudirman No. 123, Jakarta Pusat 10220, Indonesia"
      },
      stats: [
        { number: 100, label: t.stats.clients, suffix: "+", icon: Award },
        { number: 10, label: t.stats.team, suffix: "+", icon: Users },
        { number: 5, label: t.stats.years, suffix: "+", icon: History },
        { number: 10, label: t.stats.products, suffix: "+", icon: Package }
      ]
    };
    return baseData;
  }, [language, t]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      if (mapRef.current) {
        const rect = mapRef.current.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom >= 0;
        if (isVisible && !isMapVisible) {
          setIsMapVisible(true);
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMapVisible]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
      setActiveSection(id);
    }
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/6281210514213', '_blank');
  };

  if (showSplash) {
    return <SplashScreen onComplete={() => setShowSplash(false)} lang={language} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-white shadow-lg'}`}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg overflow-hidden">
                <img 
                  src={localizedData.about.logo} 
                  alt="The Horizon Logo" 
                  className="w-full h-full object-contain p-1"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<span class="text-orange-600 font-bold text-xl">TH</span>';
                  }}
                />
              </div>
              <span className={`text-2xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent ${scrolled ? '' : 'text-white'}`}>
                The Horizon Indonesia
              </span>
            </div>
            
            {/* Desktop Menu */}
            <nav className="hidden md:flex space-x-8">
              {['home', 'about', 'vision', 'values', 'expertise', 'credentials', 'products', 'team', 'contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className={`${scrolled ? 'text-gray-700 hover:text-orange-600' : 'text-gray-700 hover:text-orange-600'} transition-colors capitalize font-medium relative group`}
                >
                  {t.nav[item]}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-orange-600 group-hover:w-full transition-all duration-300"></span>
                </button>
              ))}
            </nav>

            {/* Language Switcher (Flags) - Mengganti tombol Hubungi Kami */}
            <div className="hidden md:flex items-center bg-gray-100 rounded-full p-1 border border-gray-200">
              <button 
                onClick={() => setLanguage('id')}
                className={`p-2 rounded-full transition-all duration-300 flex items-center justify-center ${language === 'id' ? 'bg-white shadow-md scale-110' : 'hover:bg-gray-200'}`}
                title="Bahasa Indonesia"
              >
                <span className="text-xl">🇮🇩</span>
              </button>
              <button 
                onClick={() => setLanguage('en')}
                className={`p-2 rounded-full transition-all duration-300 flex items-center justify-center ${language === 'en' ? 'bg-white shadow-md scale-110' : 'hover:bg-gray-200'}`}
                title="English"
              >
                <span className="text-xl">🇬🇧</span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`md:hidden ${scrolled ? 'text-gray-700' : 'text-white'}`}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 bg-white rounded-lg shadow-xl p-4">
              {/* Language Switcher for Mobile */}
              <div className="flex justify-center mb-6 space-x-4">
                <button onClick={() => setLanguage('id')} className={`text-2xl ${language === 'id' ? 'opacity-100' : 'opacity-50 hover:opacity-100'}`}>🇮🇩</button>
                <button onClick={() => setLanguage('en')} className={`text-2xl ${language === 'en' ? 'opacity-100' : 'opacity-50 hover:opacity-100'}`}>🇬🇧</button>
              </div>

              {['home', 'about', 'vision', 'values', 'expertise', 'credentials', 'products', 'team', 'contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className="block w-full text-left py-3 text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg px-4 capitalize font-medium transition-all"
                >
                  {t.nav[item]}
                </button>
              ))}
              <button
                onClick={handleWhatsApp}
                className="w-full mt-4 bg-gradient-to-r from-orange-600 to-amber-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg"
              >
                {t.hero.btnContact}
              </button>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-24 pb-20 bg-gradient-to-br from-orange-600 via-orange-500 to-amber-600 text-white overflow-hidden relative">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-6 py-20 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="max-w-3xl">
              <div className="inline-block bg-white bg-opacity-20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-6 animate-pulse">
                {t.hero.badge}
              </div>
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                {t.hero.title}
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-orange-100">
                {t.hero.subtitle}
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => scrollToSection('products')}
                  className="bg-white text-orange-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all shadow-2xl hover:shadow-3xl transform hover:scale-105 inline-flex items-center group"
                >
                  {t.hero.btnProducts}
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-orange-600 transition-all backdrop-blur-sm"
                >
                  {t.hero.btnContact}
                </button>
              </div>
            </div>
            
            {/* Peta Indonesia */}
            <div className="hidden md:flex justify-center items-center">
              <div className="relative w-full max-w-lg">
                <img 
                  src="images/indonesian-map.png" 
                  alt="Peta Indonesia"
                  className="w-full h-auto opacity-30 filter brightness-0 invert animate-pulse"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {localizedData.stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div key={idx} className="text-center group">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-600 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl transform group-hover:scale-110 transition-all">
                    <Icon className="text-white" size={32} />
                  </div>
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-2">
                    <AnimatedCounter target={stat.number} suffix={stat.suffix} />
                  </div>
                  <div className="text-gray-700 font-medium">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-gradient-to-r from-orange-600 to-amber-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
                {t.about.badge}
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
                {t.about.title}
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {localizedData.about.description}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-xl shadow-lg">
                  <Award className="text-orange-500 mb-2" size={32} />
                  <div className="text-2xl font-bold text-gray-800">
                    <AnimatedCounter target={5} suffix="+" />
                  </div>
                  <span className="text-gray-600 text-sm">{t.about.statsClients}</span>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-lg">
                  <Users className="text-orange-500 mb-2" size={32} />
                  <div className="text-2xl font-bold text-gray-800">
                    <AnimatedCounter target={10} suffix="+" />
                  </div>
                  <span className="text-gray-600 text-sm">{t.about.statsTeam}</span>
                </div>
              </div>
            </div>
            <div className="relative">
            <div className="bg-gradient-to-br from-orange-600 to-amber-600 rounded-3xl p-12 text-white shadow-2xl transform hover:scale-105 transition-all">
              <div className="flex justify-center mb-6">
                <div className="flex bg-white rounded-full items-center justify-center shadow-2xl p-6">
                  <img 
                    src={localizedData.about.logo} 
                    alt="The Horizon Logo" 
                    className="w-24 h-24 object-contain"
                  />
                </div>
              </div>
              <h3 className="text-3xl font-bold text-center mb-4">
                {t.about.cardTitle}
              </h3>
              <p className="text-center text-orange-100">
                {t.about.cardDesc}
              </p>
            </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-amber-400 rounded-full blur-2xl opacity-50"></div>
            </div>
          </div>
        </div>
      </section>

      {/* History Section */}
      <section id="history" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block p-4 bg-gradient-to-br from-orange-100 to-amber-100 rounded-full mb-4">
                <History className="text-orange-600" size={48} />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">{t.history.title}</h2>
              <p className="text-gray-600">{t.history.subtitle}</p>
            </div>
            <div className="bg-gradient-to-br from-orange-600 to-amber-600 rounded-3xl shadow-2xl p-8 md:p-12 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl"></div>
              <div className="relative z-10">
                <div className="flex items-center mb-6">
                  <div className="bg-white text-orange-600 px-8 py-3 rounded-full font-bold text-2xl shadow-lg">
                    {localizedData.history.year}
                  </div>
                </div>
                <p className="text-orange-50 text-lg leading-relaxed">
                  {localizedData.history.story}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section id="vision" className="py-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-orange-600 to-amber-600 rounded-3xl p-10 text-white shadow-2xl transform hover:scale-105 transition-all relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-white opacity-10 rounded-full blur-2xl"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-6">
                  <Target size={32} />
                </div>
                <h3 className="text-3xl md:text-4xl font-bold mb-6">{t.visionMission.vision}</h3>
                <p className="text-lg leading-relaxed text-orange-50">
                  {localizedData.vision}
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-gray-700 to-gray-900 rounded-3xl p-10 text-white shadow-2xl transform hover:scale-105 transition-all relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-orange-500 opacity-10 rounded-full blur-2xl"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-orange-500 bg-opacity-20 rounded-full flex items-center justify-center mb-6">
                  <Briefcase size={32} />
                </div>
                <h3 className="text-3xl md:text-4xl font-bold mb-6">{t.visionMission.mission}</h3>
                <ul className="space-y-4">
                  {localizedData.mission.map((item, idx) => (
                    <li key={idx} className="flex items-start group">
                      <span className="bg-gradient-to-br from-orange-600 to-amber-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1 flex-shrink-0 font-semibold shadow-lg group-hover:scale-110 transition-transform">
                        {idx + 1}
                      </span>
                      <span className="text-gray-100 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section id="values" className="py-20 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-gradient-to-r from-orange-600 to-amber-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4">
              {t.values.badge}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">{t.values.title}</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">{t.values.subtitle}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {localizedData.values.map((value, idx) => {
              const Icon = value.icon;
              const isActive = activeValue === idx;
              return (
                <div 
                  key={idx} 
                  className={`relative overflow-hidden rounded-3xl shadow-2xl cursor-pointer transition-all duration-500 transform hover:scale-105 ${isActive ? 'md:scale-105' : ''}`}
                  onClick={() => setActiveValue(isActive ? null : idx)}
                >
                  <div className={`bg-gradient-to-br ${value.color} p-8 text-white h-full`}>
                    <div className="flex justify-center mb-6">
                      <div className="w-24 h-24 bg-white bg-opacity-20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-xl">
                        <Icon size={48} />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-center mb-4">{value.title}</h3>
                    <p className="text-center text-white text-opacity-90 mb-6">{value.desc}</p>
                    {isActive && (
                      <div className="bg-white bg-opacity-10 backdrop-blur-sm p-6 rounded-2xl animate-fade-in">
                        <p className="text-white leading-relaxed mb-4">{value.detail}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Expertise Section */}
      <section id="expertise" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-gradient-to-r from-orange-600 to-amber-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4">
              {t.expertise.badge}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">{t.expertise.title}</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">{t.expertise.subtitle}</p>
          </div>
          
          <div className="space-y-8">
            {/* Strategic Studies Category */}
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden transform hover:scale-[1.02] transition-all">
              <div className="bg-gradient-to-r from-blue-700 via-blue-600 to-cyan-600 p-8 text-white">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-4">
                    <Shield size={24} />
                  </div>
                  <h3 className="text-3xl font-bold">{t.expertise.categories.strategic}</h3>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
                {localizedData.expertise.filter(item => item.category === 'strategic').map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div 
                      key={idx}
                      className={`${item.color} p-6 text-white hover:opacity-90 transition-all cursor-pointer relative group min-h-[200px] flex flex-col justify-between`}
                    >
                      <div>
                        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mb-4">
                          <Icon size={24} />
                        </div>
                        <h4 className="font-bold text-lg mb-2">{item.name}</h4>
                      </div>
                      <div className={`absolute inset-0 bg-black bg-opacity-90 p-6 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100`}>
                        <p className="text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Economic Studies Category */}
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden transform hover:scale-[1.02] transition-all">
              <div className="bg-gradient-to-r from-amber-700 via-orange-600 to-yellow-600 p-8 text-white">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-4">
                    <TrendingUp size={24} />
                  </div>
                  <h3 className="text-3xl font-bold">{t.expertise.categories.economic}</h3>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
                {localizedData.expertise.filter(item => item.category === 'economic').map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div 
                      key={idx}
                      className={`${item.color} group p-6 text-white hover:opacity-90 transition-all cursor-pointer relative min-h-[200px] flex flex-col justify-between`}
                    >
                      <div>
                        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mb-4">
                          <Icon size={24} />
                        </div>
                        <h4 className="font-bold text-lg mb-2">{item.name}</h4>
                      </div>
                      <div className={`absolute inset-0 bg-black bg-opacity-90 p-6 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 `}>
                        <p className="text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Other Solutions Category */}
            <div className="bg-gradient-to-r from-red-600 to-pink-600 rounded-3xl shadow-2xl overflow-hidden text-white p-10 transform hover:scale-[1.02] transition-all">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-4">
                  <Lightbulb size={32} />
                </div>
                <h3 className="text-3xl font-bold">{t.expertise.categories.other}</h3>
              </div>
              <h4 className="font-bold text-2xl mb-4">{t.expertise.customTitle}</h4>
              <p className="text-lg mb-6 text-white text-opacity-90">{t.expertise.customDesc}</p>
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-white text-red-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all shadow-xl inline-flex items-center group"
              >
                {t.expertise.contactBtn}
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Our Credentials Section */}
      <section id="credentials" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-gradient-to-r from-orange-600 to-amber-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4">
              {t.credentials.badge}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">{t.credentials.title}</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">{t.credentials.subtitle}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {localizedData.credentials.map((cred, idx) => {
              const isActive = activeCredential === idx;
              return (
                <div 
                  key={idx} 
                  className={`bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 ${isActive ? 'ring-4 ring-orange-500 scale-105' : ''}`}
                  onClick={() => setActiveCredential(isActive ? null : idx)}
                >
                  <div className={`h-48 bg-gradient-to-br ${cred.color} flex items-center justify-center text-white relative overflow-hidden p-6`}>
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full blur-2xl"></div>
                    </div>
                    <div className="relative z-10 w-full h-full flex items-center justify-center">
                      <img 
                        src={cred.logo} 
                        alt={cred.name}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'block';
                        }}
                      />
                      <div className="hidden text-center">
                        <div className="text-5xl font-bold mb-2">{cred.name.split(' ').map(word => word[0]).join('')}</div>
                        <div className="text-sm opacity-75">Official Partner</div>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-3">{cred.name}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{cred.description}</p>
                    {isActive && (
                      <div className="mt-4 pt-4 border-t border-gray-200 animate-fade-in">
                        <button className="text-orange-600 font-semibold flex items-center hover:text-orange-700 group">
                          {t.credentials.detailBtn} 
                          <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="text-center mt-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-600 to-amber-600 rounded-full text-white font-bold text-2xl shadow-xl">
              {localizedData.credentials.length}
            </div>
            <p className="mt-4 text-gray-600 font-medium">{t.credentials.footer}</p>
          </div>
        </div>
      </section>

      {/* Our Products Section */}
      <section id="products" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block p-4 bg-gradient-to-br from-orange-100 to-amber-100 rounded-full mb-4">
              <Package className="text-orange-600" size={48} />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">{t.products.title}</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">{t.products.subtitle}</p>
          </div>
          
          {/* Product Navigation Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {localizedData.products.map((product, idx) => {
              const Icon = product.icon;
              return (
                <button
                  key={idx}
                  onClick={() => setActiveProduct(idx)}
                  className={`px-6 py-3 rounded-full font-semibold transition-all transform hover:scale-105 inline-flex items-center ${
                    activeProduct === idx 
                      ? 'bg-gradient-to-r from-orange-600 to-amber-600 text-white shadow-xl' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="mr-2" size={18} />
                  {product.title}
                </button>
              );
            })}
          </div>
          
          {/* Product Display */}
          <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative overflow-hidden rounded-3xl shadow-2xl group">
              <div className="w-full h-96 bg-white flex items-center justify-center relative overflow-hidden">
                <img 
                  src={localizedData.products[activeProduct].image} 
                  alt={localizedData.products[activeProduct].title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                <div className={`hidden w-full h-full bg-gradient-to-br ${localizedData.products[activeProduct].gradient} items-center justify-center text-white absolute inset-0`}>
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
                  </div>
                  <div className="relative z-10 text-center p-8">
                    {(() => {
                      const Icon = localizedData.products[activeProduct].icon;
                      return <Icon size={120} className="mx-auto mb-4" />;
                    })()}
                    <h3 className="text-3xl font-bold">{localizedData.products[activeProduct].title}</h3>
                  </div>
                </div>
              </div>
              <div className="absolute top-6 right-6 bg-white px-4 py-2 rounded-full text-sm font-semibold text-gray-800 shadow-lg">
                {localizedData.products[activeProduct].category}
              </div>
            </div>
            
            <div className="flex flex-col justify-between space-y-6">
              <div>
                <div className="inline-block bg-gradient-to-r from-orange-100 to-amber-100 text-orange-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  {localizedData.products[activeProduct].type === 'book' ? t.products.typePub : t.products.typeService}
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">{localizedData.products[activeProduct].title}</h3>
                <p className="text-gray-600 text-lg mb-6 leading-relaxed">{localizedData.products[activeProduct].description}</p>
                <p className="text-gray-700 leading-relaxed">{localizedData.products[activeProduct].details}</p>
              </div>
              
              <div>
                <button className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white px-8 py-4 rounded-full font-semibold transition-all shadow-xl hover:shadow-2xl inline-flex items-center group transform hover:scale-105">
                  {(() => {
                    const Icon = localizedData.products[activeProduct].icon;
                    return <Icon className="mr-2" size={20} />;
                  })()}
                  {localizedData.products[activeProduct].type === 'book' ? t.products.btnBuy : t.products.btnContact}
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </button>
              </div>
            </div>
          </div>
          
          {/* Product Gallery */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-gray-800 mb-8 text-center">{t.products.otherTitle}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {localizedData.products.map((product, idx) => {
                const Icon = product.icon;
                return (
                  <div 
                    key={idx}
                    onClick={() => setActiveProduct(idx)}
                    className={`relative overflow-hidden rounded-2xl cursor-pointer transition-all transform hover:scale-105 ${
                      activeProduct === idx ? 'ring-4 ring-orange-500 shadow-2xl' : 'shadow-lg'
                    }`}
                  >
                    <div className="h-40 bg-white flex items-center justify-center relative overflow-hidden">
                      <img 
                        src={product.image} 
                        alt={product.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      <div className={`hidden bg-gradient-to-br ${product.gradient} w-full h-full items-center justify-center text-white absolute inset-0`}>
                        <Icon size={48} />
                      </div>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-white text-sm font-semibold text-center">
                      {product.title}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-20 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block p-4 bg-gradient-to-br from-orange-100 to-amber-100 rounded-full mb-4">
              <Users className="text-orange-600" size={48} />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">{t.team.title}</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">{t.team.subtitle}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {localizedData.team.map((member, idx) => (
              <div key={idx} className="text-center group">
                <div className={`w-32 h-32 bg-gradient-to-br ${member.color} rounded-2xl mx-auto mb-4 flex items-center justify-center overflow-hidden shadow-xl group-hover:shadow-2xl transition-all group-hover:scale-110 group-hover:rotate-3 relative`}>
                  <img 
                    src={member.photo} 
                    alt={member.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-br items-center justify-center text-white font-bold text-3xl hidden">
                    {member.initials}
                  </div>
                </div>
                <h4 className="text-base font-bold text-gray-800 mb-1">{member.name}</h4>
                <p className="text-sm text-gray-600 leading-tight">{member.position}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-orange-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-500 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">{t.contact.title}</h2>
              <p className="text-gray-300 text-lg">{t.contact.subtitle}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-600 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl group-hover:shadow-2xl group-hover:scale-110 transition-all">
                  <Phone size={32} />
                </div>
                <h4 className="font-bold text-xl mb-2">{t.contact.phone}</h4>
                <p className="text-gray-300">{localizedData.contact.phone}</p>
              </div>
              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-600 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl group-hover:shadow-2xl group-hover:scale-110 transition-all">
                  <Mail size={32} />
                </div>
                <h4 className="font-bold text-xl mb-2">{t.contact.email}</h4>
                <p className="text-gray-300">{localizedData.contact.email}</p>
              </div>
              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-600 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl group-hover:shadow-2xl group-hover:scale-110 transition-all">
                  <MapPin size={32} />
                </div>
                <h4 className="font-bold text-xl mb-2">{t.contact.address}</h4>
                <p className="text-gray-300">{localizedData.contact.address}</p>
              </div>
            </div>
            <div className="text-center">
              <button
                onClick={handleWhatsApp}
                className="bg-green-500 hover:bg-green-600 text-white px-12 py-5 rounded-full font-bold text-lg shadow-2xl hover:shadow-3xl transition-all inline-flex items-center group transform hover:scale-105"
              >
                <Phone className="mr-3 group-hover:rotate-12 transition-transform" size={28} />
                {t.contact.waBtn}
                <ArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" size={24} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg overflow-hidden">
                  <img 
                    src={localizedData.about.logo} 
                    alt="The Horizon Logo" 
                    className="w-full h-full object-contain p-1"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<span class="text-orange-600 font-bold text-xl">TH</span>';
                    }}
                  />
                </div>
                <span className="text-xl font-bold">The Horizon Indonesia</span>
              </div>
              <p className="text-gray-400 mb-4">{t.footer.desc}</p>
              <p className="text-gray-500 text-sm">{t.footer.copyright}</p>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">{t.footer.links}</h4>
              <div className="space-y-2">
                {['about', 'vision', 'expertise', 'products', 'team', 'contact'].map(item => (
                  <button 
                    key={item} 
                    onClick={() => scrollToSection(item)}
                    className="block text-gray-400 hover:text-orange-500 transition-colors capitalize"
                  >
                    {t.nav[item]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">{t.footer.connect}</h4>
              <div className="space-y-3 mb-6">
                <a href={`tel:${localizedData.contact.phone}`} className="flex items-center text-gray-400 hover:text-orange-500 transition-colors group">
                  <div className="w-10 h-10 bg-gray-800 group-hover:bg-gradient-to-br group-hover:from-orange-600 group-hover:to-amber-600 rounded-lg flex items-center justify-center mr-3 transition-all">
                    <Phone size={18} />
                  </div>
                  <span className="text-sm">{localizedData.contact.phone}</span>
                </a>
                <a href={`mailto:${localizedData.contact.email}`} className="flex items-center text-gray-400 hover:text-orange-500 transition-colors group">
                  <div className="w-10 h-10 bg-gray-800 group-hover:bg-gradient-to-br group-hover:from-orange-600 group-hover:to-amber-600 rounded-lg flex items-center justify-center mr-3 transition-all">
                    <Mail size={18} />
                  </div>
                  <span className="text-sm">{localizedData.contact.email}</span>
                </a>
                <a href="https://www.linkedin.com/company/thehorizon2021/" target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-400 hover:text-orange-500 transition-colors group">
                  <div className="w-10 h-10 bg-gray-800 group-hover:bg-gradient-to-br group-hover:from-orange-600 group-hover:to-amber-600 rounded-lg flex items-center justify-center mr-3 transition-all">
                    <Linkedin size={18} />
                  </div>
                  <span className="text-sm">LinkedIn</span>
                </a>
                <a href="https://horizon-indonesia.com/" target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-400 hover:text-orange-500 transition-colors group">
                  <div className="w-10 h-10 bg-gray-800 group-hover:bg-gradient-to-br group-hover:from-orange-600 group-hover:to-amber-600 rounded-lg flex items-center justify-center mr-3 transition-all">
                    <Globe2 size={18} />
                  </div>
                  <span className="text-sm">horizon-indonesia.com</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <button
        onClick={handleWhatsApp}
        className="fixed bottom-8 right-8 bg-green-500 hover:bg-green-600 text-white w-16 h-16 rounded-full shadow-2xl hover:shadow-3xl transition-all flex items-center justify-center z-50 hover:scale-110 transform animate-bounce-slow"
      >
        <Phone size={28} />
      </button>
    </div>
  );
};

export default CompanyProfile;